import { useEffect, useMemo, useState } from "react";
import { Loader2, ShoppingBag } from "lucide-react";
import { useHerCollectionCart } from "@/stores/herCollectionCart";
import HerCollectionBag from "@/components/support/HerCollectionBag";
import { supabase } from "@/integrations/supabase/client";
import { fetchAthleteCollection, formatMoney, numericShopifyId, type CollectionProduct } from "@/lib/shopify";

const ProductCard = ({ product, athleteId, athleteName }: { product: CollectionProduct; athleteId: string; athleteName: string }) => {
  const addLine = useHerCollectionCart((s) => s.addLine);
  const isLoading = useHerCollectionCart((s) => s.isLoading);
  const addError = useHerCollectionCart((s) => s.error);
  const [selected, setSelected] = useState<Record<string, string>>(() =>
    Object.fromEntries((product.variants[0]?.selectedOptions ?? []).map((o) => [o.name, o.value])),
  );
  const variant = useMemo(
    () =>
      product.variants.find((v) => v.selectedOptions.every((o) => selected[o.name] === o.value)) ?? product.variants[0],
    [product, selected],
  );
  const available = !!variant?.availableForSale;
  return (
    <li className="rounded-xl border border-border bg-background overflow-hidden min-w-0">
      <div className="aspect-square bg-muted">
        {product.image ? (
          <img src={product.image.url} alt={product.image.altText ?? product.title} className="w-full h-full object-cover" loading="lazy" />
        ) : null}
      </div>
      <div className="p-3 space-y-2">
        <h3 className="font-semibold text-sm leading-tight break-words">{product.title}</h3>
        <p className="font-display text-lg">{variant ? formatMoney(variant.price) : formatMoney(product.priceRange.minVariantPrice)}</p>
        {product.options.map((opt) => (
          <fieldset key={opt.name} className="min-w-0">
            <legend className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{opt.name}</legend>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {opt.values.map((val) => {
                const active = selected[opt.name] === val;
                return (
                  <button
                    key={val}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setSelected((s) => ({ ...s, [opt.name]: val }))}
                    className={`min-h-[36px] min-w-[44px] px-2.5 rounded-full border text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                      active ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"
                    }`}
                  >
                    {val}
                  </button>
                );
              })}
            </div>
          </fieldset>
        ))}
        <p className={`text-xs font-semibold ${available ? "text-primary" : "text-muted-foreground"}`}>
          {available ? "In stock" : "Sold out"}
        </p>
        <button
          type="button"
          disabled={!available || !variant || isLoading}
          onClick={() =>
            variant &&
            addLine({
              variantId: variant.id, productId: product.id, productTitle: product.title, variantTitle: variant.title,
              imageUrl: product.image?.url ?? null, price: variant.price, quantity: 1,
              selectedOptions: variant.selectedOptions, athleteId, athleteName,
            })
          }
          className="w-full min-h-[44px] rounded-full bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {available ? "Add to bag" : "Sold out"}
        </button>
        {addError && <p className="text-xs text-destructive" role="alert">{addError}</p>}
      </div>
    </li>
  );
};

/** Real Shopify products tagged with siis.athlete_id. Display only — checkout not enabled yet. */
const HerCollection = ({ athleteId, athleteName, preview }: { athleteId: string; athleteName: string; preview: boolean }) => {
  const [showBag, setShowBag] = useState(false);
  const bagCount = useHerCollectionCart((s) => s.lines.reduce((n, l) => n + l.quantity, 0));
  const bagToggle = (
    <button type="button" onClick={() => setShowBag((v) => !v)} aria-expanded={showBag}
      className="inline-flex items-center gap-2 min-h-[44px] text-sm font-semibold underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded">
      <ShoppingBag className="w-4 h-4" aria-hidden /> {showBag ? "Back to collection" : `View bag (${bagCount})`}
    </button>
  );
  const [state, setState] = useState<{ status: "loading" | "ready" | "error"; products: CollectionProduct[] }>({
    status: "loading",
    products: [],
  });
  useEffect(() => {
    let alive = true;
    (async () => {
      // Approved SIIS product records (RLS: admins + the athlete herself; live RPC later for public).
      const { data, error } = await supabase
        .from("merch_products" as never)
        .select("shopify_product_id")
        .eq("athlete_id", athleteId)
        .in("approval_status", ["approved", "live"])
        .is("archived_at", null);
      if (error) throw error;
      const ids = new Set(((data ?? []) as { shopify_product_id: string }[]).map((r) => numericShopifyId(r.shopify_product_id)));
      return fetchAthleteCollection(athleteId, ids);
    })()
      .then((products) => alive && setState({ status: "ready", products }))
      .catch(() => alive && setState({ status: "error", products: [] }));
    return () => {
      alive = false;
    };
  }, [athleteId]);
  if (showBag) return (<>{bagToggle}<HerCollectionBag preview={preview} /></>);

  if (state.status === "loading")
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
        <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> Loading her collection…
      </p>
    );
  if (state.status === "error")
    return <p className="text-sm text-muted-foreground" role="status">Her collection couldn't load right now. Please try again later.</p>;
  if (state.products.length === 0)
    return (
      <p className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground" role="status">
        No products found{preview ? " for this athlete yet. Products appear here once a Shopify product's siis.athlete_id matches her SIIS ID." : "."}
      </p>
    );
  return (
    <>
      {bagToggle}
      <ul className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-3">
        {state.products.map((p) => (
          <ProductCard key={p.id} product={p} athleteId={athleteId} athleteName={athleteName} />
        ))}
      </ul>
      <p className="text-xs text-muted-foreground">Checkout isn't open yet.</p>
    </>
  );
};

export default HerCollection;
